/**
 * @module AdaptersPage
 * @package @geenius/adapters/react-native
 * @description Renders the native adapters overview page.
 */

import type {
  AdapterDomain,
  AdapterStatusType,
  DomainAdapterConfig,
} from "@geenius/adapters";
import {
  ADAPTER_DOMAINS,
  ADAPTER_STATUSES,
  DOMAIN_LABELS,
  getDomainConfig,
  isAdaptersConfigured,
} from "@geenius/adapters";
import type { JSX } from "react";
import { useState } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { ErrorBoundaryBridge } from "../bridges/ErrorBoundaryBridge";
import { semanticColors } from "../bridges/TokenBridge";
import {
  AdapterConfigForm,
  AdapterList,
  AdapterStatusBadge,
} from "../components";
import { useAdapters } from "../hooks";
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "../lib/native";

type RegistryEntry = {
  domain: AdapterDomain;
  provider: string;
  status: AdapterStatusType;
};

const adapterDomains = ADAPTER_DOMAINS as readonly AdapterDomain[];
const adapterStatuses = ADAPTER_STATUSES as readonly AdapterStatusType[];

/**
 * Props for the native adapters overview page.
 *
 * @property configByDomain Optional configuration snapshot used to hydrate modal forms.
 * @property onNavigateDetail Optional navigation callback for external routers.
 * @property onConfigure Optional callback invoked with submitted adapter configuration.
 * @property reducedMotion Disables modal slide animation when `true`.
 * @property registryEntries Optional provider registry rows shown above the list.
 * @property style Optional native style merged onto the page container.
 * @property testID Optional deterministic selector for tests and Storybook.
 */
export interface AdaptersPageProps {
  configByDomain?: Partial<Record<AdapterDomain, DomainAdapterConfig>>;
  onNavigateDetail?: (domain: AdapterDomain) => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  reducedMotion?: boolean;
  registryEntries?: RegistryEntry[];
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * Renders the native overview dashboard for all adapter domains.
 *
 * @param props Overview page props including optional navigation and registry rows.
 * @returns A React Native adapters page with metrics, filters, and configuration modal.
 * @example
 * ```tsx
 * <AdaptersPage onNavigateDetail={(domain) => router.push(domain)} />
 * ```
 */
export function AdaptersPage(props: AdaptersPageProps): JSX.Element {
  const { reducedMotion = false, style } = props;
  return (
    <ErrorBoundaryBridge style={style} testID="adapters-page-error-boundary">
      <AdaptersPageContent
        configByDomain={props.configByDomain}
        onNavigateDetail={props.onNavigateDetail}
        onConfigure={props.onConfigure}
        reducedMotion={reducedMotion}
        registryEntries={props.registryEntries}
        style={props.style}
        testID={props.testID}
      />
    </ErrorBoundaryBridge>
  );
}

function AdaptersPageContent({
  configByDomain,
  onNavigateDetail,
  onConfigure,
  reducedMotion,
  registryEntries,
  style,
  testID,
}: Required<Pick<AdaptersPageProps, "reducedMotion">> &
  Omit<AdaptersPageProps, "reducedMotion">): JSX.Element {
  const { statuses, isLoading } = useAdapters();
  const [selectedDomain, setSelectedDomain] = useState<AdapterDomain | null>(
    null,
  );
  const [filterStatus, setFilterStatus] = useState<
    AdapterStatusType | undefined
  >();
  const initialConfigFor = (domain: AdapterDomain) =>
    configByDomain?.[domain] ??
    (isAdaptersConfigured() ? getDomainConfig(domain) : undefined) ??
    undefined;

  const counts = {
    connected: adapterDomains.filter(
      (domain: AdapterDomain) => statuses[domain]?.status === "connected",
    ).length,
    error: adapterDomains.filter(
      (domain: AdapterDomain) => statuses[domain]?.status === "error",
    ).length,
    total: adapterDomains.length,
  };

  const handleSelect = (domain: AdapterDomain) => {
    if (onNavigateDetail) {
      onNavigateDetail(domain);
      return;
    }
    setSelectedDomain(domain);
  };

  return (
    <ScrollView
      accessibilityLabel="Adapters overview"
      contentContainerStyle={[styles.page, style]}
      keyboardShouldPersistTaps="handled"
      testID={testID ?? "adapters-page"}
    >
      <View style={styles.header}>
        <Text accessibilityRole="header" style={styles.title}>
          Adapters
        </Text>
        <Text style={styles.subtitle}>
          Manage database, auth, AI, storage, payments, admin, and logging.
        </Text>
      </View>

      <View style={styles.metrics}>
        <View style={styles.metricCard}>
          <Text style={styles.metricLabel}>Total</Text>
          <Text style={styles.metricValue}>{counts.total}</Text>
          <Text style={styles.metricHint}>adapter domains</Text>
        </View>
        <View style={[styles.metricCard, styles.connectedMetric]}>
          <Text style={styles.metricLabel}>Connected</Text>
          <Text style={styles.metricValue}>{counts.connected}</Text>
          <Text style={styles.metricHint}>active adapters</Text>
        </View>
        {counts.error > 0 && (
          <View style={[styles.metricCard, styles.errorMetric]}>
            <Text style={styles.metricLabel}>Errors</Text>
            <Text style={styles.metricValue}>{counts.error}</Text>
            <Text style={styles.metricHint}>need attention</Text>
          </View>
        )}
      </View>

      <View style={styles.filters}>
        <Pressable
          accessibilityLabel="Show all adapters"
          accessibilityRole="button"
          accessibilityState={{ selected: filterStatus === undefined }}
          onPress={() => setFilterStatus(undefined)}
          style={[styles.filter, !filterStatus && styles.activeFilter]}
          testID="adapter-filter-all"
        >
          <Text style={styles.filterText}>All</Text>
        </Pressable>
        {adapterStatuses.map((status: AdapterStatusType) => (
          <Pressable
            accessibilityLabel={`Filter adapters by ${status}`}
            accessibilityRole="button"
            accessibilityState={{ selected: filterStatus === status }}
            key={status}
            onPress={() =>
              setFilterStatus((current: AdapterStatusType | undefined) =>
                current === status ? undefined : status,
              )
            }
            style={[
              styles.filter,
              filterStatus === status && styles.activeFilter,
            ]}
            testID={`adapter-filter-${status}`}
          >
            <Text style={styles.filterText}>{status}</Text>
          </Pressable>
        ))}
      </View>

      {registryEntries && registryEntries.length > 0 && (
        <View style={styles.registry}>
          <Text style={styles.sectionLabel}>Adapter Registry</Text>
          {registryEntries.map((entry) => (
            <View
              key={`${entry.domain}-${entry.provider}`}
              style={styles.registryRow}
            >
              <View>
                <Text style={styles.registryDomain}>{entry.domain}</Text>
                <Text style={styles.registryProvider}>{entry.provider}</Text>
              </View>
              <AdapterStatusBadge status={entry.status} />
            </View>
          ))}
        </View>
      )}

      <AdapterList
        filterStatus={filterStatus}
        onSelect={handleSelect}
        testID={isLoading ? "adapters-list-loading" : "adapters-list"}
      />

      <Modal
        animationType={reducedMotion ? "none" : "slide"}
        onRequestClose={() => setSelectedDomain(null)}
        testID="adapter-config-modal"
        transparent
        visible={selectedDomain !== null}
      >
        <View style={styles.modalBackdrop}>
          <View
            accessibilityLabel={
              selectedDomain
                ? `Configure ${DOMAIN_LABELS[selectedDomain]} adapter`
                : "Configure adapter"
            }
            accessibilityViewIsModal
            accessibilityRole="summary"
            style={styles.modalPanel}
            testID="adapter-config-modal-panel"
          >
            {selectedDomain && (
              <AdapterConfigForm
                domain={selectedDomain}
                initialConfig={initialConfigFor(selectedDomain)}
                onCancel={() => setSelectedDomain(null)}
                onSave={async (config) => {
                  await onConfigure?.(selectedDomain, config);
                  setSelectedDomain(null);
                }}
              />
            )}
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  activeFilter: {
    backgroundColor: semanticColors.brand.bgSoft,
    borderColor: semanticColors.brand.border,
  },
  connectedMetric: {
    backgroundColor: semanticColors.status.connected.bg,
    borderColor: semanticColors.status.connected.border,
  },
  errorMetric: {
    backgroundColor: semanticColors.status.error.bg,
    borderColor: semanticColors.status.error.border,
  },
  filter: {
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.border,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  filterText: {
    color: semanticColors.surface.textSoft,
    fontSize: 12,
    fontWeight: "800",
    textTransform: "capitalize",
  },
  filters: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  header: {
    gap: 4,
  },
  metricCard: {
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.border,
    borderRadius: 10,
    borderWidth: 1,
    flex: 1,
    minWidth: 110,
    padding: 14,
  },
  metricHint: {
    color: semanticColors.surface.textMuted,
    fontSize: 11,
    marginTop: 2,
  },
  metricLabel: {
    color: semanticColors.surface.textMuted,
    fontSize: 11,
    fontWeight: "800",
    textTransform: "uppercase",
  },
  metricValue: {
    color: semanticColors.surface.text,
    fontSize: 26,
    fontWeight: "900",
    marginTop: 4,
  },
  metrics: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  modalBackdrop: {
    backgroundColor: semanticColors.surface.overlay,
    flex: 1,
    justifyContent: "flex-end",
  },
  modalPanel: {
    backgroundColor: semanticColors.surface.bg,
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    padding: 16,
  },
  page: {
    backgroundColor: semanticColors.surface.bg,
    gap: 18,
    padding: 16,
  },
  registry: {
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.border,
    borderRadius: 10,
    borderWidth: 1,
    gap: 10,
    padding: 14,
  },
  registryDomain: {
    color: semanticColors.surface.text,
    fontSize: 13,
    fontWeight: "800",
  },
  registryProvider: {
    color: semanticColors.surface.textMuted,
    fontSize: 12,
  },
  registryRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  sectionLabel: {
    color: semanticColors.surface.textMuted,
    fontSize: 11,
    fontWeight: "800",
    textTransform: "uppercase",
  },
  subtitle: {
    color: semanticColors.surface.textMuted,
    fontSize: 14,
    lineHeight: 20,
  },
  title: {
    color: semanticColors.surface.text,
    fontSize: 28,
    fontWeight: "900",
  },
});
